import UserMessageEditIcon from '@/assets/icons/pencil.svg'
import { ChatAttachments } from '@/components/chat/ChatAttachments'
import MarkdownContent from '@/components/chat/MarkdownContent'
import { Button } from '@/components/ui/button'
import { useEditMessage } from '@/hooks/useApi'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/useAuthStore'
import { useChatStore } from '@/store/useChatStore'
import type { ChatMessage } from '@/types/chat'
import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

interface UserChatMessageProps {
  message: ChatMessage
  canEdit?: boolean
  className?: string
}

export function UserChatMessage({ message, canEdit = true, className }: UserChatMessageProps) {
  const tCommon = useTranslations('common')
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(message.content)
  const editMessageAction = useChatStore((s) => s.editMessage)
  const { mutateAsync: editMessageFn, isPending, invalidate } = useEditMessage()
  const attachments = (message.attachments ?? []).filter((a) => a.role === 'question')
  const user = useAuthStore((s) => s.user)

  const userName = user ? `${user.first_name} ${user.last_name}`.trim() || user.username : ''
  const userInitial = userName.charAt(0).toUpperCase()
  const timestamp = message.created ? format(new Date(message.created), 'HH:mm') : ''

  if (!message.content && attachments.length === 0) return null

  const handleStartEdit = () => {
    setEditContent(message.content)
    setIsEditing(true)
  }

  const handleCancel = () => setIsEditing(false)

  const handleSubmit = async () => {
    const trimmed = editContent.trim()
    if (!trimmed || trimmed === message.content) {
      setIsEditing(false)
      return
    }
    await editMessageAction({
      conversationId: message.id,
      content: trimmed,
      editMessage: editMessageFn,
      onComplete: invalidate,
    })
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') handleCancel()
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit()
  }

  const bubbleClass =
    'flex max-w-full flex-col gap-2 border border-main-border-lv03 rounded-tl-100 rounded-tr-400 rounded-bl-400 rounded-br-400 bg-main-support-surface-secondary pl-4 pr-10 py-3 shadow-elevation-lv03'

  const header = (
    <div className="flex items-center gap-2">
      <div className="flex items-center justify-center rounded-200 bg-main-support-surface-blue">
        <div className="flex size-8 items-center justify-center">
          <span className="text-ui-title-t1 font-semibold leading-ui-title-t1 text-main-support-on-surface-on-blue">
            {userInitial}
          </span>
        </div>
      </div>
      <span className="text-ui-title-t3 font-semibold leading-ui-title-t3 text-black">{userName}</span>
    </div>
  )

  if (isEditing) {
    return (
      <div className="flex w-full flex-col gap-2">
        {header}
        <div className="flex w-full justify-end">
          <div className={cn(bubbleClass, 'w-full', className)}>
            <textarea
              className="w-full resize-none bg-transparent text-ui-caption-c1 leading-ui-caption-c1 text-main-text-lv01 outline-none"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={Math.max(2, editContent.split('\n').length)}
              autoFocus
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isPending}>
                {tCommon('cancel')}
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={isPending || !editContent.trim()}>
                {isPending ? tCommon('saving') : tCommon('save')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {header}
      <div className="flex w-full items-center justify-start gap-2.5">
        <div className={cn(bubbleClass, className)}>
          <div className="flex flex-col gap-2">
            <div className="**:text-ui-caption-c1 **:leading-ui-caption-c1 **:text-main-text-lv01">
              <MarkdownContent content={message.content} />
            </div>
            <ChatAttachments attachments={attachments} />
          </div>
          {timestamp && (
            <div className="py-2">
              <span className="text-ui-caption-c2 leading-ui-caption-c2 text-main-text-lv03">{timestamp}</span>
            </div>
          )}
        </div>
        {canEdit && message.id >= 0 && (
          <Button variant="ghost" size="icon-md" onClick={handleStartEdit} disabled={isPending}>
            <UserMessageEditIcon className="size-4 text-[#7E8B99]" />
          </Button>
        )}
      </div>
    </div>
  )
}
